import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { isIosApp } from '@/native/capacitorApp';

/**
 * Sign in with Apple 시작점.
 *
 * - 웹/Android: Apple JS(appleid.auth.js)를 동적 로드해 `appleid.apple.com` 으로 리다이렉트한다.
 *   Apple 은 form_post 로 서버 콜백(`/api/auth/social/apple/callback`)에 결과를 보내고,
 *   서버가 다시 프론트 `/login/callback/apple` 로 302 시킨다.
 * - iOS 앱: 네이티브 시트(ASAuthorizationController)로 즉시 결과(identityToken)를 받는다.
 *
 * 두 경로 모두 `startAppleSignIn()` 하나로 감싸고, 실제 로그인 API 호출·분기는
 * `/login/callback/apple`(AppleCallback) 한 곳에서만 처리한다(중복 방지).
 */

/** 웹 client_id (Apple Services ID). 값은 Apple Developer 에 등록된 Services ID 와 같아야 한다. */
export const APPLE_SERVICES_ID: string = import.meta.env.VITE_APPLE_SERVICES_ID || 'com.myungchisa.web';

/** Apple 에 등록된 Return URL — 서버 콜백 엔드포인트. 네이티브 authorize 에도 같은 값을 넘긴다. */
export const APPLE_REDIRECT_URI: string =
    import.meta.env.VITE_APPLE_REDIRECT_URI || 'https://www.myungchisa.com/api/auth/social/apple/callback';

/** iOS 네이티브 client_id — 앱 번들 ID(identity token 의 aud). */
const APPLE_BUNDLE_ID = 'com.myungchisa.app';

/** CSRF 방어용 state 보관 키 (카카오/네이버와 같은 관례). */
export const APPLE_STATE_STORAGE_KEY = 'apple_oauth_state';

// 로케일 경로는 언더스코어 형식(en_US/ko_KR)만 유효하다 — 하이픈(en-US)은 404. 서비스 언어에 맞춰 ko_KR 사용.
const APPLE_JS_SRC = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/ko_KR/appleid.auth.js';

interface AppleIdAuthInitConfig {
    clientId: string;
    scope: string;
    redirectURI: string;
    state: string;
    nonce?: string;
    usePopup: boolean;
}

declare global {
    interface Window {
        AppleID?: {
            auth: {
                init(config: AppleIdAuthInitConfig): void;
                signIn(): Promise<unknown>;
            };
        };
    }
}

/** 서버 `POST /api/auth/social/apple/login` 요청 바디. */
export interface AppleLoginRequest {
    identityToken: string;
    authorizationCode?: string;
    client: 'web' | 'ios';
    /** Apple 이 최초 인증 때만 주는 이름/이메일 — 가입 폼 프리필용. */
    user?: { name?: string; email?: string };
    state?: string;
}

/**
 * 로그인 시작 결과.
 * - `redirecting`: 웹 — Apple 페이지로 이동 중(이후 코드는 실행되지 않는다고 보면 된다)
 * - `cancelled`: 사용자가 취소 — 아무 안내도 띄우지 않는다
 * - `native`: iOS — 이 payload 로 서버 로그인 API 를 호출하면 된다
 */
export type AppleSignInStart =
    | { status: 'redirecting' }
    | { status: 'cancelled' }
    | { status: 'native'; payload: AppleLoginRequest };

let appleScriptPromise: Promise<void> | null = null;

/** Apple JS 를 1회만 로드한다(같은 세션에서 버튼을 여러 번 눌러도 script 태그는 하나). */
function loadAppleScript(): Promise<void> {
    if (window.AppleID) {
        return Promise.resolve();
    }
    if (!appleScriptPromise) {
        appleScriptPromise = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = APPLE_JS_SRC;
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => {
                // 실패한 promise 를 캐시하면 재시도가 막히므로 비워둔다
                appleScriptPromise = null;
                reject(new Error('Apple JS 로드 실패'));
            };
            document.head.appendChild(script);
        });
    }
    return appleScriptPromise;
}

/** 사용자가 애플 시트/페이지를 직접 닫은 경우 — 에러 안내 없이 조용히 끝낸다. */
function isUserCancelled(error: unknown): boolean {
    const raw = error as { code?: string | number; message?: string; error?: string } | null;
    const code = String(raw?.code ?? '');
    const message = `${raw?.message ?? ''} ${raw?.error ?? ''}`.toLowerCase();
    // iOS ASAuthorizationError.canceled = 1001, 웹 Apple JS = popup_closed_by_user / user_cancelled_authorize
    return (
        code === '1001' ||
        message.includes('1001') ||
        message.includes('cancel') ||
        message.includes('popup_closed_by_user')
    );
}

/** OAuth 왕복 후에도 원래 보던 페이지로 복귀할 수 있도록 보존 (콜백에서 소비). */
function rememberReturnUrl(from?: string): void {
    if (from && from !== '/') {
        sessionStorage.setItem('login_return_url', from);
    } else {
        sessionStorage.removeItem('login_return_url');
    }
}

/**
 * Apple 로그인 시작. 웹은 리다이렉트, iOS 앱은 네이티브 시트.
 *
 * @param from 로그인 완료 후 돌아갈 경로(선택)
 * @throws Apple JS 로드 실패·네이티브 인증 오류 등 (취소는 예외가 아니라 `cancelled` 로 반환)
 */
export async function startAppleSignIn(from?: string): Promise<AppleSignInStart> {
    const state: string = crypto.randomUUID();
    sessionStorage.setItem(APPLE_STATE_STORAGE_KEY, state);
    rememberReturnUrl(from);

    if (isIosApp()) {
        try {
            const result = await SignInWithApple.authorize({
                clientId: APPLE_BUNDLE_ID,
                redirectURI: APPLE_REDIRECT_URI,
                scopes: 'email name',
                state,
                nonce: crypto.randomUUID(),
            });
            const response = result.response;
            // 한국어 이름은 성(familyName) + 이름(givenName) 순서로 붙인다.
            const name: string = `${response.familyName ?? ''}${response.givenName ?? ''}`.trim();
            return {
                status: 'native',
                payload: {
                    identityToken: response.identityToken,
                    authorizationCode: response.authorizationCode || undefined,
                    client: 'ios',
                    user: {
                        name: name || undefined,
                        email: response.email || undefined,
                    },
                    state,
                },
            };
        } catch (error) {
            if (isUserCancelled(error)) {
                return { status: 'cancelled' };
            }
            throw error;
        }
    }

    await loadAppleScript();
    const appleId = window.AppleID;
    if (!appleId) {
        throw new Error('Apple JS 초기화 실패');
    }
    appleId.auth.init({
        clientId: APPLE_SERVICES_ID,
        scope: 'name email',
        redirectURI: APPLE_REDIRECT_URI,
        state,
        usePopup: false,
    });
    try {
        // usePopup:false 이므로 여기서 Apple 페이지로 이동한다(정상 흐름에선 resolve 되지 않는다).
        await appleId.auth.signIn();
    } catch (error) {
        if (isUserCancelled(error)) {
            return { status: 'cancelled' };
        }
        throw error;
    }
    return { status: 'redirecting' };
}

/**
 * 이 환경에서 Apple 로그인을 제공할 수 있는지.
 * iOS 앱은 네이티브 플러그인(빌드 7 이상)이 있어야 하고, 구버전 앱(빌드 6 이하)에는 플러그인이 없어
 * 버튼을 눌러도 "시작할 수 없습니다"만 뜬다 → 버튼 자체를 숨긴다. Android 앱은 미노출, 웹은 항상 가능.
 */
export function isAppleSignInAvailable(): boolean {
    if (isIosApp()) {
        return Capacitor.isPluginAvailable('SignInWithApple');
    }
    // Android 앱: Apple 로그인 미노출(사용자 결정 2026-09-12) — 웹뷰에서 Apple 페이지가 크롬으로 새는 문제를 피하고,
    // Android 는 카카오·이메일만 제공. 웹 브라우저는 항상 가능.
    if (Capacitor.isNativePlatform()) {
        return false;
    }
    return true;
}
